import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type NotificationsData } from '@core/api/notifications.api';
import type { EntityId } from '@core/api/types';
import { qk } from '@core/queries/keys';
import { cn } from '@core/utils/cn';
import { Icon } from '@ui/Icon';
import { IconButton } from '@ui/IconButton';

/**
 * Notification bell + dropdown. Ports the `notificationMenu` block of
 * `header.component.html` (lines 12-53) and the fetch/mark logic from
 * `header.component.ts`.
 *
 * Behaviour preserved: fetched ONCE on mount, no polling and no websocket, exactly as
 * the Angular header did. Adding `refetchInterval` would be a behaviour change and is
 * tracked as a P10 decision rather than smuggled in here.
 *
 * The optimistic local-array mutation the Angular component did by hand becomes an
 * optimistic `setQueryData`, which also rolls back correctly on failure.
 */
export function NotificationBell() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: qk.notifications(),
    queryFn: () => notificationsApi.getNotifications(),
    staleTime: 60_000,
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;

  const markAsRead = useMutation({
    mutationFn: (notificationId: EntityId) => notificationsApi.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: qk.notifications() });
      const previous = queryClient.getQueryData<NotificationsData>(qk.notifications());

      queryClient.setQueryData<NotificationsData>(qk.notifications(), (current) =>
        current
          ? {
              ...current,
              unread_count: Math.max(0, current.unread_count - 1),
              notifications: current.notifications.map((item) =>
                item.notification_id === notificationId ? { ...item, is_read: true } : item,
              ),
            }
          : current,
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(qk.notifications(), context.previous);
      }
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.setQueryData<NotificationsData>(qk.notifications(), (current) =>
        current
          ? {
              ...current,
              unread_count: 0,
              notifications: current.notifications.map((item) => ({ ...item, is_read: true })),
            }
          : current,
      );
    },
  });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton
          label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          size="lg"
          className="relative"
        >
          <Icon size="lg">notifications</Icon>
          {unreadCount > 0 && (
            <span className="vm-notification-badge" aria-hidden>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </IconButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="vm-menu-panel z-[1200] w-[360px] max-w-[92vw]"
        >
          <div className="vm-menu-header">
            <Icon size="lg">notifications</Icon>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="ml-auto cursor-pointer rounded-xs px-1.5 py-0.5 text-[11px] text-white hover:bg-white/15"
                onClick={(event) => {
                  // Keep the panel open, as the Angular handler's stopPropagation did.
                  event.preventDefault();
                  event.stopPropagation();
                  markAllAsRead.mutate();
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="vm-notification-list">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <DropdownMenu.Item
                  key={notification.notification_id}
                  className={cn(
                    'vm-menu-item vm-notification-item',
                    !notification.is_read && 'unread',
                  )}
                  onSelect={() => {
                    // Skip the request when already read — Angular did the same.
                    if (!notification.is_read) {
                      markAsRead.mutate(notification.notification_id);
                    }
                  }}
                >
                  <Icon size="md">
                    {notification.is_read ? 'notifications_none' : 'notifications_active'}
                  </Icon>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-base font-semibold">{notification.title}</span>
                    <span className="text-xs font-normal text-fg-2">{notification.message}</span>
                  </span>
                </DropdownMenu.Item>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 px-5 py-8 text-[#94a3b8]">
                <Icon className="text-[32px] text-[#cbd5e1]">info_outline</Icon>
                <span className="text-base font-medium">No new notifications</span>
              </div>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
